import { useCallback, useEffect, useState } from 'react';
import { useRevalidator } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { SafeHtml } from '@hsp/react-component-library';
import { isDefined, isNotDefined, isOfType, sort } from '@hsp/utilities';

import {
  IExportStatusPayload,
  IStatusPayload,
  NotificationTarget,
  QuantityAudiencePayload,
  QuantityPayload,
} from '@c/notifications/types';

import { sharedABQueryKeys } from '@p/ab/shared/utilities';
import { dashboardQueryKeys } from '@p/audience/dashboard/hooks/queries';

import { AudienceShopStatus, IAudience, IAudienceShop } from '@t/api/audience';
import { IResult } from '@t/api/common';

import { notifications } from '@u/notifications';

import { useSignalREffect } from 'hooks/signalr';

export const useAudienceBuilderBroadcasts = () => {
  const [reload, setReload] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const revalidator = useRevalidator();

  const handleQuantity = useCallback(
    (payload: QuantityPayload) => {
      const { audienceId, quantity, shopId, status } = payload;
      let quantityStatus: AudienceShopStatus | undefined;
      let total = 0;

      if (isDefined(audienceId)) {
        queryClient.setQueryData<IResult<IAudienceShop[]>>(dashboardQueryKeys.shops.byId(audienceId), (cached) => {
          if (isDefined(cached)) {
            const { dataContainer } = cached;

            if (isNotDefined(dataContainer)) {
              return cached;
            }

            const index = dataContainer.findIndex((item) => item.shopId === shopId);

            if (index >= 0) {
              cached = {
                ...cached,
                dataContainer: [
                  ...dataContainer.slice(0, index),
                  { ...dataContainer[index], quantity, status: { id: status, name: '' } },
                  ...dataContainer.slice(index + 1),
                ],
              };
            }

            quantityStatus =
              sort(
                [
                  ...(cached.dataContainer ?? []).map(({ status }) => {
                    const currentStatus = status?.id ?? AudienceShopStatus.QuantityPending;
                    let order = 2;

                    switch (currentStatus) {
                      case AudienceShopStatus.QuantityFailed:
                        order = 1;
                        break;

                      case AudienceShopStatus.QuantityPending:
                        order = 2;
                        break;

                      case AudienceShopStatus.QuantityUpdated:
                        order = 3;
                        break;
                    }

                    return {
                      order,
                      status: currentStatus,
                    };
                  }),
                ],
                { direction: 'asc', field: 'order' }
              ).shift()?.status ?? AudienceShopStatus.QuantityPending;

            total = (cached.dataContainer ?? []).reduce(
              (previous, { quantity: currentQuantity }) => (previous += currentQuantity),
              0
            );

            return cached;
          }

          return cached;
        });

        queryClient.setQueriesData<IResult<IAudience[]>>({ queryKey: dashboardQueryKeys.audiences.all }, (cached) => {
          if (isDefined(cached)) {
            const { dataContainer } = cached;

            if (isNotDefined(dataContainer)) {
              return cached;
            }

            const index = dataContainer.findIndex((item) => item.id === audienceId);

            if (index >= 0) {
              const { category } = dataContainer[index];

              if (isNotDefined(quantityStatus) && isDefined(category)) {
                quantityStatus = status;
                total = quantity;
              }

              cached = {
                ...cached,
                dataContainer: [
                  ...dataContainer.slice(0, index),
                  {
                    ...dataContainer[index],
                    quantity: total,
                    quantityStatus,
                  },
                  ...dataContainer.slice(index + 1),
                ],
              };
            }

            return cached;
          }

          return cached;
        });

        queryClient.setQueryData<IResult<IAudienceShop>>(sharedABQueryKeys.shop.default(shopId), (cached) => {
          if (isDefined(cached)) {
            const { dataContainer } = cached;

            if (isNotDefined(dataContainer)) {
              return cached;
            }

            const { shopId: cachedShopId } = dataContainer;

            if (cachedShopId === shopId) {
              cached = {
                ...cached,
                dataContainer: {
                  ...dataContainer,
                  quantity,
                  status: {
                    id: status,
                    name: AudienceShopStatus[status],
                  },
                },
              };

              setReload(true);
            }
          }

          return cached;
        });
      }
    },
    [queryClient]
  );

  const handleQuantityAudience = useCallback(
    (payload: QuantityAudiencePayload) => {
      const { audienceId, quantity, shops, status } = payload;

      queryClient.setQueryData<IResult<IAudienceShop[]>>(dashboardQueryKeys.shops.byId(audienceId), (cached) => {
        if (isDefined(cached)) {
          const { dataContainer } = cached;

          if (isNotDefined(dataContainer)) {
            return cached;
          }

          for (const { quantity: shopQuantity, shopId } of shops) {
            const index = dataContainer.findIndex(({ shopId: id }) => id === shopId);

            if (index !== -1) {
              cached = {
                ...cached,
                dataContainer: [
                  ...dataContainer.slice(0, index),
                  { ...dataContainer[index], quantity: shopQuantity, status: { id: status, name: '' } },
                  ...dataContainer.slice(index + 1),
                ],
              };
            }
          }
        }

        return cached;
      });

      queryClient.setQueriesData<IResult<IAudience[]>>({ queryKey: dashboardQueryKeys.audiences.all }, (cached) => {
        if (isDefined(cached)) {
          const { dataContainer } = cached;

          if (isNotDefined(dataContainer)) {
            return cached;
          }

          const index = dataContainer.findIndex(({ id }) => id === audienceId);

          if (index !== -1) {
            cached = {
              ...cached,
              dataContainer: [
                ...dataContainer.slice(0, index),
                {
                  ...dataContainer[index],
                  quantity,
                },
                ...dataContainer.slice(index + 1),
              ],
            };
          }

          return cached;
        }

        return cached;
      });
    },
    [queryClient]
  );

  const handleStatus = useCallback(
    (payload: IStatusPayload) => {
      const { id, status } = payload;

      queryClient.setQueriesData<IResult<IAudience[]>>({ queryKey: dashboardQueryKeys.audiences.all }, (cached) => {
        if (isDefined(cached)) {
          const { dataContainer } = cached;

          if (isNotDefined(dataContainer)) {
            return cached;
          }

          const index = dataContainer.findIndex((item) => item.id === id);

          if (index >= 0) {
            const target = dataContainer[index];

            cached = {
              ...cached,
              dataContainer: [
                ...dataContainer.slice(0, index),
                {
                  ...target,
                  shippedDate: isOfType<IExportStatusPayload>(payload, 'shippedDate')
                    ? payload.shippedDate
                    : target.shippedDate,
                  status: { id: status, name: '' },
                },
                ...dataContainer.slice(index + 1),
              ],
            };
          }

          return cached;
        }

        return cached;
      });

      queryClient.setQueryData<IResult<IAudienceShop[]>>(dashboardQueryKeys.shops.byId(id), (cached) => {
        if (isDefined(cached)) {
          const { dataContainer } = cached;

          if (isNotDefined(dataContainer)) {
            return cached;
          }

          cached = {
            ...cached,
            dataContainer: dataContainer.map((item) => ({ ...item, statusId: status })),
          };

          return cached;
        }

        return cached;
      });
    },
    [queryClient]
  );

  useSignalREffect(
    NotificationTarget.AudienceStatus,
    (notification) => {
      if (isNotDefined(notification)) {
        return;
      }

      const { isError } = notification;

      if (isError) {
        return;
      }

      const { payload } = notification;

      if (isNotDefined(payload)) {
        return;
      }

      handleStatus(payload);
    },
    []
  );

  useSignalREffect(
    NotificationTarget.Export,
    (notification) => {
      if (isNotDefined(notification)) {
        return;
      }

      const { isError } = notification;

      if (isError) {
        return;
      }

      const { payload } = notification;

      if (isNotDefined(payload)) {
        return;
      }

      handleStatus(payload);
    },
    []
  );

  useSignalREffect(
    NotificationTarget.ExportForUser,
    (notification) => {
      if (isNotDefined(notification)) {
        return;
      }

      const { id: toastId, isError, message } = notification;

      if (isError) {
        return;
      }

      const { payload } = notification;

      if (isDefined(message) && isDefined(payload)) {
        const { fileName, shareFileUri } = payload;

        notifications.info(
          <div className="d-flex flex-column">
            <div>{message}</div>
            <div className="mt-3">
              <a
                href={shareFileUri}
                rel="noreferrer"
                target="_blank">
                {fileName}
              </a>
            </div>
          </div>,
          undefined,
          {
            toastId,
          }
        );
      }
    },
    []
  );

  useSignalREffect(
    NotificationTarget.Quantity,
    (notification) => {
      if (isNotDefined(notification)) {
        return;
      }

      const { isError } = notification;

      if (isError) {
        return;
      }

      const { payload } = notification;

      if (isNotDefined(payload)) {
        return;
      }

      handleQuantity(payload);
    },
    []
  );

  useSignalREffect(
    NotificationTarget.QuantityAudience,
    (notification) => {
      if (isNotDefined(notification)) {
        return;
      }

      const { isError } = notification;

      if (isError) {
        return;
      }

      const { payload } = notification;

      if (isNotDefined(payload)) {
        return;
      }

      handleQuantityAudience(payload);
    },
    []
  );

  useSignalREffect(
    NotificationTarget.QuantityForUser,
    (notification) => {
      if (isNotDefined(notification)) {
        return;
      }

      const { id: toastId, isError, message } = notification;

      if (isDefined(message)) {
        if (isError) {
          notifications.error(<SafeHtml html={message} />, undefined, { toastId });
        } else {
          notifications.info(<SafeHtml html={message} />, undefined, { toastId });
        }
      }
    },
    []
  );

  useSignalREffect(
    NotificationTarget.Snapshot,
    (notification) => {
      if (isNotDefined(notification)) {
        return;
      }

      const { id: toastId, isError, message } = notification;

      if (isDefined(message)) {
        if (isError) {
          notifications.error(<SafeHtml html={message} />, undefined, { toastId });
        } else {
          notifications.info(<SafeHtml html={message} />, undefined, { toastId });
        }
      }
    },
    []
  );

  useEffect(() => {
    if (reload) {
      revalidator.revalidate();
      setReload(false);
    }
  }, [reload, revalidator]);
};
